import { call, put, select } from 'redux-saga/effects';
import { api, showSuccessNotification } from '@med-fe/util';
import {
  fetchTemplateDetailsFailed,
  fetchTemplateDetailsSuccess,
  fetchLiveRangeTemplateDetailsFailed,
  fetchLiveRangeTemplateDetailsSuccess,
  searchTemplatesFailed,
  searchTemplatesSuccess,
  removeTemplateFailed,
  removeTemplateSuccess,
  searchTemplates,
  renameTemplateSuccess,
  renameTemplateFailed,
  importFileSuccess,
  importFileFailed,
  cleanImportLayout,
  updateValidatedSuccess,
  updateValidatedFailed,
  unmappOfficeSuccess,
  unmappOfficeFailed,
  setExamTypes,
  cloneTemplateDetailsSuccess,
  cloneTemplateDetailsFailed,
  updateLiveRange0TemplateSlotDetailsSuccess,
  updateLiveRange0TemplateSlotDetailsFailed,
  updateLiveSpecialRangeTemplateSlotDetailsSuccess,
  updateLiveSpecialRangeTemplateSlotDetailsFailed,
  updateTemplateSlotDetailsSuccess,
  updateTemplateSlotDetailsFailed,
  checkTemplateNameSuccess,
  checkTemplateNameFailed,
  createTemplateDetailsSuccess,
  createTemplateDetailsFailed,
  createTemplateDraftDetailsSuccess,
  createTemplateDraftDetailsFailed,
  searchTemplateDrafts,
  searchTemplateDraftsSuccess,
  searchTemplateDraftsFailed,
  removeTemplateDraftsSuccess,
  removeTemplateDraftsFailed,
  fetchTemplateDraftDetails,
  fetchTemplateDraftDetailsSuccess,
  fetchTemplateDraftDetailsFailed,
  updateTemplateDraftSlotDetailsSuccess,
  updateTemplateDraftSlotDetailsFailed,
  publishDraftSuccess,
  publishDraftFailed,
  bulkCloneTemplatesSuccess,
  bulkCloneTemplatesFailed,
} from './templates-actions';
import { showErrorNotification } from '@med-fe/util';
import { getImportValidationData, getSearch } from './templates-selector';

export function* searchTemplatesStartWorker({ payload }): Generator {
  try {
    const url = `/templates/list?page=${payload.page}&size=${payload.size}`;
    const data = yield call(api.post, url, payload, null, true, false);
    yield put(searchTemplatesSuccess(data));
  } catch (e) {
    yield put(searchTemplatesFailed(e));
    showErrorNotification();
  }
}

export function* fetchTemplateDetailsStartWorker({ payload }): Generator {
  try {
    const data = yield call(api.get, `/templates/${payload.id}`, true);
    yield put(fetchTemplateDetailsSuccess(data));
  } catch (e) {
    yield put(fetchTemplateDetailsFailed(e));
    showErrorNotification();
  }
}

export function* fetchLiveRangeTemplateDetailsStartWorker({ payload }): Generator {
  try {
    //console.log(payload);
    let url = `/live-ranges/${payload.id}`;
    if (payload.id === '-1') {
      url = `/live-ranges/range0/${payload.id}?employeeNum=${payload.employeeNum}&officeNum=${payload.officeNum}&dayOfWeek=${payload.dayOfWeek}&templateName=${payload.templateName}`;
    }
    const data = yield call(api.get, url, true);
    yield put(fetchLiveRangeTemplateDetailsSuccess(data));
  } catch (e) {
    yield put(fetchLiveRangeTemplateDetailsFailed(e));
    showErrorNotification();
  }
}

export function* fetchTemplateDraftDetailsStartWorker({ payload }): Generator {
  try {
    const data = yield call(api.get, `/templatedrafts/${payload.id}`, true);
    // reuse the same success action so DraftDetails can consume the shape
    yield put(fetchTemplateDraftDetailsSuccess(data));
  } catch (e) {
    yield put(fetchTemplateDraftDetailsFailed(e));
    showErrorNotification();
  }
}

export function* setExamTypesStartWorker({ payload }): Generator {
  try {
    const data = yield call(api.get, `/GetAllExamTypes`, true, false);
    yield put(setExamTypes(data));
  } catch (e) {
    showErrorNotification();
  }
}

export function* removeTemplateStartWorker({ payload }): Generator {
  let data;
  try {
    const search: any = yield select(getSearch);
    data = yield call(api.delete, `/templates/delete`, payload.ids, true, false);
    yield put(removeTemplateSuccess(data));
    yield put(searchTemplates(search));

    if (payload.successCallback) {
      yield call(payload.successCallback);
    }

    showSuccessNotification('Template(s) successfully deleted');
  } catch (e: any) {
    yield put(removeTemplateFailed(e));
    showErrorNotification(e.reason || null);
  }
}

export function* renameTemplateNameStartWorker({ payload }): Generator {
  try {
    yield call(api.patch, `/templates/rename?id=${payload.id}&newName=${payload.newName}`);
    // Fetch the latest template details after renaming
    const data = yield call(api.get, `/templates/${payload.id}`, true, false);
    yield put(renameTemplateSuccess(data.templateName));
    yield put({ type: 'FETCH_TEMPLATE_DETAILS_SUCCESS', payload: data });
    showSuccessNotification('The template was successfully renamed');
  } catch (e) {
    yield put(renameTemplateFailed(e));
    showErrorNotification();
  }
}

export function* importBulkUpdateFileStartWorker({ payload, actionType }: any): Generator {
  try {
    let url;
    if (actionType === 'Delete Ranges') {
      url = '/templates/delete-ranges/upload';
    } else if (actionType === 'Template Maintenance') {
      url = '/templates/maintenance/upload';
    } else if (actionType === 'Resource cleanup') {
      url = '/resourcecleanup/upload';
    } else {
      url = '/doctors/assign-template/upload';
    }
    const formdata = new FormData();
    formdata.append('file', payload.file);
    const data: any = yield call(api.post, url, formdata);
    if (actionType === 'Resource cleanup' && data === '') {
      yield put(importFileSuccess(null, 0, null));
    } else {
      yield put(importFileSuccess(data.uploadDto, data.totalCount, data.validationErrors));
    }
  } catch (e: any) {
    yield put(importFileFailed(e.reason));
    if (actionType !== 'Resource cleanup') showErrorNotification(e.reason, null, 5);
  }
}

export function* updateValidatedImportStartWorker({ payload, actionType, flag }: any): Generator {
  try {
    let url;
    if (actionType === 'Delete Ranges') {
      url = '/templates/delete-ranges/submit';
    } else if (actionType === 'Template Maintenance') {
      url = '/templates/maintenance/submit';
    } else {
      url = '/doctors/assign-template/submit?' + flag;
    }
    const data: any = yield call(api.post, url, payload);
    if (actionType === 'Assign Templates') {
      yield put(updateValidatedSuccess(data));
    } else {
      yield put(cleanImportLayout());
      showSuccessNotification(data, 3);
    }
  } catch (e: any) {
    yield put(updateValidatedFailed(e.reason));
    showErrorNotification(e.reason);
  }
}

export function* unmapingOffice(payload: any): Generator {
  try {
    const url = '/doctors/unmap';
    const data: any = yield call(api.post, url, payload?.payload);
    yield put(unmappOfficeSuccess(data));
    if (data === 1) showSuccessNotification('Office unmapped successfully');
    else showErrorNotification('Cannot Unmap, No OD-Office record found.');
  } catch (e) {
    console.log(e);
    yield put(unmappOfficeFailed(e));
    showErrorNotification();
  }
}

export function* cloneTemplateDetailsStartWorker({ payload }): Generator {
  try {
    const data = yield call(api.post, `/templates/clone`, payload, null, true, false);
    yield put(cloneTemplateDetailsSuccess(data));
    showSuccessNotification('Template cloned successfully');
  } catch (e) {
    yield put(cloneTemplateDetailsFailed(e));
    showErrorNotification(e.error.messages ? e.error.messages[0] : 'Error while cloning template.');
  }
}

export function* updateTemplateDetailsStartWorker({ payload }): Generator {
  try {
    const data = yield call(api.post, `/templates/update`, payload, null, true, false);
    yield put(updateTemplateSlotDetailsSuccess(data));
    showSuccessNotification('Template updated successfully');
  } catch (e) {
    yield put(updateTemplateSlotDetailsFailed(e));
    showErrorNotification();
  }
}

export function* updateLiveSpecialRangeTemplateDetailsStartWorker({ payload }): Generator {
  try {
    const data = yield call(api.post, `/live-ranges/special-update`, payload, null, true, false);
    yield put(updateLiveSpecialRangeTemplateSlotDetailsSuccess(data));
    showSuccessNotification('Live Special Range Template updated successfully');
  } catch (e) {
    yield put(updateLiveSpecialRangeTemplateSlotDetailsFailed(e));
    showErrorNotification();
  }
}

export function* updateLiveRange0TemplateDetailsStartWorker({ payload }): Generator {
  try {
    const data = yield call(api.post, `/live-ranges/range0-update`, payload, null, true, false);
    yield put(updateLiveRange0TemplateSlotDetailsSuccess(data));
    showSuccessNotification('Live Range0 Template updated successfully');
  } catch (e) {
    yield put(updateLiveRange0TemplateSlotDetailsFailed(e));
    showErrorNotification();
  }
}

export function* updateTemplateDraftDetailsStartWorker({ payload }): Generator {
  try {
    const data = yield call(api.post, `/templates/updatedraft`, payload, null, true, false);
    yield put(updateTemplateDraftSlotDetailsSuccess(data));
    showSuccessNotification('Draft updated successfully');
  } catch (e) {
    yield put(updateTemplateDraftSlotDetailsFailed(e));
    showErrorNotification();
  }
}

export function* createTemplateDetailsStartWorker({ payload }): Generator {
  try {
    const search: any = yield select(getSearch);
    const data = yield call(api.post, `/templates/create`, payload, null, true, false);
    yield put(createTemplateDetailsSuccess(data));
    yield put(searchTemplates(search));
    showSuccessNotification('Template created successfully');
  } catch (e) {
    yield put(createTemplateDetailsFailed(e));
    showErrorNotification();
  }
}

export function* createTemplateDraftDetailsStartWorker({ payload }): Generator {
  try {
    const search: any = yield select(getSearch);
    const data = yield call(api.post, `/templates/createdraft`, payload, null, true, false);
    yield put(createTemplateDraftDetailsSuccess(data));
    yield put(searchTemplateDrafts(search));
    showSuccessNotification('Template Draft created successfully');
  } catch (e) {
    yield put(createTemplateDraftDetailsFailed(e));
    showErrorNotification();
  }
}

export function* checkTemplateNameWorker({ payload }): Generator {
  try {
    const res = yield call(api.post, '/templates/CheckTemplateName', payload, null, true, false);
    yield put(checkTemplateNameSuccess({ valid: !res }));
  } catch (e) {
    yield put(checkTemplateNameFailed(e));
  }
}

export function* searchTemplateDraftsStartWorker({ payload }): Generator {
  try {
    const url = `/templates/draftlist?page=${payload.page}&size=${payload.size}`;
    const data = yield call(api.post, url, payload, null, true, false);
    yield put(searchTemplateDraftsSuccess(data));
  } catch (e) {
    yield put(searchTemplateDraftsFailed(e));
    showErrorNotification();
  }
}

export function* removeTemplateDraftsStartWorker({ payload }): Generator {
  try {
    const search: any = yield select(getSearch);
    // assume backend expects an array of draft ids at endpoint /templates/delete-drafts
    const data = yield call(api.delete, `/templates/delete-drafts`, payload.ids, true, false);
    yield put(removeTemplateDraftsSuccess(data));
    // refresh drafts list
    yield put(searchTemplateDrafts(search));
    if (payload.successCallback) {
      yield call(payload.successCallback);
    }
    showSuccessNotification('Draft(s) successfully deleted');
  } catch (e: any) {
    yield put(removeTemplateDraftsFailed(e));
    showErrorNotification(e.reason || null);
  }
}

export function* publishDraftStartWorker({ payload }): Generator {
  try {
    const search: any = yield select(getSearch);
    const data = yield call(api.post, `/templates/publishdraft`, payload, null, true, false);
    yield put(publishDraftSuccess(data));
    yield put(searchTemplateDrafts(search));
    showSuccessNotification('Draft published successfully');
  } catch (e) {
    yield put(publishDraftFailed(e));
    showErrorNotification();
  }
}

export function* bulkCloneTemplatesStartWorker({ payload }): Generator {
  try {
    const search: any = yield select(getSearch);
    // assume backend expects an array of draft ids at endpoint /templates/delete-drafts
    const data = yield call(
      api.post,
      `/templates/bulkclone`,
      {
        templateIdList: payload.selectedRows.map((row) => +row.key.split('-')[0]),
      },
      null,
      true,
      false
    );
    var failedTemplates: string = '';
    if (data && data.templateIdList && data.templateIdList.length > 0) {
      failedTemplates = data.templateIdList
        .map((t) => payload.selectedRows.find((x) => x.template.id == t).template.templateName.trim())
        .join(',');
    }
    if (failedTemplates) {
      showErrorNotification(`The following template(s) could not be cloned: ${failedTemplates}`);
    } else {
      showSuccessNotification(`Template${payload.selectedRows.length > 1 ? 's' : ''} successfully cloned`);
    }
    yield put(bulkCloneTemplatesSuccess(data));
    // refresh drafts list
    yield put(searchTemplates(search));
    if (payload.successCallback) {
      yield call(payload.successCallback);
    }
  } catch (e: any) {
    yield put(bulkCloneTemplatesFailed(e));
    showErrorNotification(e.reason || null);
  }
}
